import BigNumber from "bignumber.js";
import {
  IExactGetOperation,
  ISwapPathInfo,
  quoteExactGetPrice,
  SwapRequest,
} from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "../../../../utils/helper";
import { getApiSwapPath } from "../Content/helpers/getApiSwapPath";
import { useCreateSwapRequestContext } from "../context";
import { TabTypes } from "../types";
import { useQuote } from "./useQuote";
import { getCommonDisplayDecimals } from "../helpers/getCommonDisplayDecimals";

interface IProps {
  swapPaths: ISwapPathInfo[];
}

export const useExactGetQuote = (props: IProps) => {
  const { swapPaths } = props;

  const { filterFields, exactGetFields, setExactGetFields } =
    useCreateSwapRequestContext();

  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const { selectChainByPieces } = useChains();
  const { merchantId } = useAppSelector(selectProfile);
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const fromCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.fromCurrency
      )
    ) || 6;

  const toCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.toCurrency
      )
    ) || 6;

  const quotePayload: SwapRequest<IExactGetOperation> = {
    merchant_id: Number(merchantId),
    chain_type: Number(chain_type),
    chain_id: Number(chain_id),
    asset_name: filterFields.toCurrency,
    asset_name_pay: filterFields.fromCurrency,
    operations: [
      {
        operation_seq: "1",
        amount: amountToCryptoAmount(
          BigNumber(exactGetFields.exactGetAmount)
            .decimalPlaces(getCommonDisplayDecimals(toCurrencyDecimals), 1)
            .toFixed(),
          toCurrencyDecimals
        ),
        decimals: toCurrencyDecimals,
        swap_path: getApiSwapPath(
          swapPaths,
          filterFields.fromCurrency,
          filterFields.toCurrency
        ),
      },
    ],
  };

  const validateExactGetQuote = () => {
    if (!filterFields.fromCurrency || !filterFields.toCurrency) {
      alerting("warning", t("psCurrencyPair"));
      return false;
    }

    if (!exactGetFields.exactGetAmount) {
      alerting("warning", t("peExactGetAmount"));
      return false;
    }

    return true;
  };

  const { handleQuote } = useQuote({
    fields: exactGetFields,
    setFields: setExactGetFields,
    mode: TabTypes.EXACT_GET,
    payload: quotePayload,
    quoteApiFn: quoteExactGetPrice,
    validateFunc: validateExactGetQuote,
  });

  return { handleQuote };
};
